import {
  BINARY_LABEL,
  CIRCULAR_VALUE,
  MAX_STRING_LENGTH,
  PII_KEY_SUFFIXES,
  REDACTED_VALUE,
  TRUNCATED_SUFFIX,
} from '../constants';

type ScrubContext = {
  piiFieldsLower: string[];
  redactAll?: boolean;
  visited: WeakSet<object>;
};

type KeyMatchesPiiFieldParams = {
  keyLower: string;
  piiFieldsLower: string[];
};

/**
 * Returns true when `key` should be redacted based on the configured PII
 * field list. A key matches when its lowercased form either equals one of
 * `piiFieldsLower` exactly, or ends in one of the curated credential
 * suffixes in {@link PII_KEY_SUFFIXES} (currently `token` and `jwt`). The
 * suffix check is what catches `mfaToken`, `minifiedJwt`, `accessToken`,
 * etc. without needing every variant enumerated explicitly.
 */
const keyMatchesPiiField = ({
  keyLower,
  piiFieldsLower,
}: KeyMatchesPiiFieldParams): boolean => {
  if (piiFieldsLower.includes(keyLower)) {
    return true;
  }

  return PII_KEY_SUFFIXES.some((suffix) => keyLower.endsWith(suffix));
};

type ScrubParametersParams = {
  piiFields: string[];
  redactAll?: boolean;
  value: Record<string, unknown>;
};

type ScrubStringParams = {
  key: string;
  piiFieldsLower: string[];
  redactAll?: boolean;
  value: string;
};

type ScrubArrayParams = {
  context: ScrubContext;
  value: unknown[];
};

type ScrubObjectParams = {
  context: ScrubContext;
  value: Record<string, unknown>;
};

type ScrubValueParams = {
  context: ScrubContext;
  key: string;
  value: unknown;
};

// eslint-disable-next-line custom-rules/one-function-per-file
const scrubString = ({
  key,
  piiFieldsLower,
  redactAll,
  value,
}: ScrubStringParams): string => {
  if (keyMatchesPiiField({ keyLower: key.toLowerCase(), piiFieldsLower })) {
    return REDACTED_VALUE;
  }

  if (redactAll) {
    return REDACTED_VALUE;
  }

  if (value.length > MAX_STRING_LENGTH) {
    return value.slice(0, MAX_STRING_LENGTH) + TRUNCATED_SUFFIX;
  }

  return value;
};

// eslint-disable-next-line custom-rules/one-function-per-file
const scrubArray = ({ context, value }: ScrubArrayParams): unknown[] => {
  if (context.visited.has(value)) {
    return [CIRCULAR_VALUE];
  }

  context.visited.add(value);

  const result = value.map((item, index) =>
    scrubValue({ context, key: String(index), value: item })
  );

  context.visited.delete(value);

  return result;
};

// eslint-disable-next-line custom-rules/one-function-per-file
const scrubObject = ({
  context,
  value,
}: ScrubObjectParams): Record<string, unknown> => {
  if (context.visited.has(value)) {
    return { [CIRCULAR_VALUE]: true };
  }

  context.visited.add(value);

  // Object.create(null) prevents prototype pollution when keys like '__proto__'
  // are present (e.g. from a Map converted to a plain object).
  const result = Object.create(null) as Record<string, unknown>;

  for (const key of Object.keys(value)) {
    result[key] = scrubValue({ context, key, value: value[key] });
  }

  context.visited.delete(value);

  return result;
};

// eslint-disable-next-line custom-rules/one-function-per-file
const scrubValue = ({ context, key, value }: ScrubValueParams): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Uint8Array) {
    return `[${BINARY_LABEL}:${value.byteLength} bytes]`;
  }

  if (
    keyMatchesPiiField({
      keyLower: key.toLowerCase(),
      piiFieldsLower: context.piiFieldsLower,
    })
  ) {
    return REDACTED_VALUE;
  }

  if (typeof value === 'string') {
    return scrubString({
      key,
      piiFieldsLower: context.piiFieldsLower,
      redactAll: context.redactAll,
      value,
    });
  }

  if (Array.isArray(value)) {
    return scrubArray({ context, value });
  }

  if (value instanceof Set) {
    if (context.visited.has(value)) {
      return [CIRCULAR_VALUE];
    }

    context.visited.add(value);
    const setResult = scrubArray({ context, value: [...value] });
    context.visited.delete(value);

    return setResult;
  }

  if (value instanceof Map) {
    if (context.visited.has(value)) {
      return { [CIRCULAR_VALUE]: true };
    }

    context.visited.add(value);
    // Object.create(null) avoids prototype pollution when Map keys include
    // '__proto__', 'constructor', or 'prototype'
    const asObject = Object.create(null) as Record<string, unknown>;

    for (const [k, v] of value.entries()) {
      asObject[String(k)] = v;
    }

    const mapResult = scrubObject({ context, value: asObject });
    context.visited.delete(value);

    return mapResult;
  }

  // WeakMap and WeakSet are not iterable and expose no `.size` or enumeration
  // methods by design — their entries are held weakly and are not accessible
  // programmatically. We emit a tagged placeholder that identifies the type so
  // the log record remains self-describing without attempting enumeration.
  if (value instanceof WeakMap) {
    return '[WeakMap]';
  }

  if (value instanceof WeakSet) {
    return '[WeakSet]';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return `[Error: ${value.message}]`;
  }

  if (typeof value === 'object') {
    return scrubObject({ context, value: value as Record<string, unknown> });
  }

  return value;
};

/** @notInstrumented */
// eslint-disable-next-line custom-rules/one-function-per-file
export const scrubParameters = ({
  piiFields,
  redactAll,
  value,
}: ScrubParametersParams): Record<string, unknown> => {
  const context: ScrubContext = {
    piiFieldsLower: piiFields.map((field) => field.toLowerCase()),
    redactAll,
    visited: new WeakSet<object>(),
  };

  return scrubObject({ context, value });
};
